"use client";

import { ChevronDown, SearchLg } from "@untitledui/icons";
import type { FC, ReactNode, RefAttributes } from "react";
import { isValidElement, useCallback, useRef, useState } from "react";
import { useFilter } from "react-aria";
import type { Selection } from "react-aria-components";
import {
	Autocomplete as AriaAutocomplete,
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Input as AriaInput,
	ListBox as AriaListBox,
	Popover as AriaPopover,
	SearchField as AriaSearchField,
} from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import { SelectItem } from "./select-item";
import {
	type CommonProps,
	SelectContext,
	type SelectItemType,
	sizes,
} from "./select-shared";

function resolveTriggerIcon(icon: FC | ReactNode | undefined): ReactNode {
	if (isReactComponent(icon)) {
		const IconComponent = icon;
		return <IconComponent aria-hidden="true" data-icon />;
	}

	if (isValidElement(icon)) {
		return icon;
	}

	return null;
}

function getSelectedCount(
	selectedKeys: Selection | undefined,
	itemsLength: number
): number {
	if (selectedKeys instanceof Set) {
		return selectedKeys.size;
	}

	if (selectedKeys === "all") {
		return itemsLength;
	}

	return 0;
}

const searchSizes = {
	sm: {
		wrapper: "py-1",
		root: "px-3 py-2 gap-2 *:data-icon:size-4 *:data-icon:stroke-[2.25px]",
		text: "text-sm",
	},
	md: {
		wrapper: "py-0.5",
		root: "px-3 py-2 gap-2 *:data-icon:size-5",
		text: "text-md",
	},
	lg: {
		wrapper: "py-0.5",
		root: "px-3.5 py-2.5 gap-2 *:data-icon:size-5",
		text: "text-md",
	},
};

const footerButtonSize = {
	sm: "xs" as const,
	md: "sm" as const,
	lg: "sm" as const,
};

const popoverMaxHeights = {
	sm: "max-h-68",
	md: "max-h-76",
	lg: "max-h-92",
};

type MultiSelectFooterProps = {
	/**
	 * The size of the footer buttons.
	 * @default "sm"
	 */
	size?: "sm" | "md" | "lg";
	/** Handler that is called when the reset button is clicked. */
	onReset?: () => void;
	/** Handler that is called when the select all button is clicked. */
	onSelectAll?: () => void;
	/** Additional class name. */
	className?: string;
};

const MultiSelectFooter = ({
	size = "sm",
	onReset,
	onSelectAll,
	className,
}: MultiSelectFooterProps) => {
	const btnSize = footerButtonSize[size];

	return (
		<div
			className={cx(
				"flex items-center justify-between border-secondary border-t p-3",
				className
			)}
		>
			<Button color="secondary" onClick={onReset} size={btnSize}>
				Reset
			</Button>
			<Button color="secondary" onClick={onSelectAll} size={btnSize}>
				Select all
			</Button>
		</div>
	);
};

type MultiSelectEmptyStateProps = {
	/**
	 * The title to display.
	 * @default "No results found"
	 */
	title?: string;
	/**
	 * The description to display.
	 * @default "Please try a different search term."
	 */
	description?: string;
	/** Handler that is called when the clear search button is clicked. */
	onClearSearch?: () => void;
	/** Additional class name. */
	className?: string;
};

const MultiSelectEmptyState = ({
	title = "No results found",
	description = "Please try a different search term.",
	onClearSearch,
	className,
}: MultiSelectEmptyStateProps) => (
	<div className={cx("flex flex-col items-center gap-3 px-4 py-4", className)}>
		<div className="flex flex-col items-center gap-3">
			<FeaturedIcon color="gray" icon={SearchLg} size="sm" theme="modern" />
			<div className="flex flex-col items-center gap-0.5 text-center text-sm">
				<p className="font-semibold text-primary">{title}</p>
				<p className="text-tertiary">{description}</p>
			</div>
		</div>
		{!!onClearSearch && (
			<Button color="link-color" onClick={onClearSearch} size="sm">
				Limpar busca
			</Button>
		)}
	</div>
);

interface MultiSelectProps extends RefAttributes<HTMLDivElement>, CommonProps {
	/** The items to display in the listbox. */
	items?: SelectItemType[];
	/** The children to render for each item. */
	children: ReactNode | ((item: SelectItemType) => ReactNode);
	/** The currently selected keys (controlled). */
	selectedKeys?: Selection;
	/** The initial selected keys (uncontrolled). */
	defaultSelectedKeys?: Selection;
	/** Handler that is called when the selection changes. */
	onSelectionChange?: (keys: Selection) => void;
	/** Whether the select is disabled. */
	isDisabled?: boolean;
	/** Whether the select is required. */
	isRequired?: boolean;
	/** Whether the select is in an invalid state. */
	isInvalid?: boolean;
	/** Additional class name for the popover. */
	popoverClassName?: string;
	/** Additional class name for the root element. */
	className?: string;
	/** Handler that is called when the reset button is clicked. */
	onReset?: () => void;
	/** Handler that is called when the select all button is clicked. */
	onSelectAll?: () => void;
	/**
	 * Whether to show the footer with reset and select all buttons.
	 * @default true
	 */
	showFooter?: boolean;
	/**
	 * Whether to show the search input in the popover.
	 * @default true
	 */
	showSearch?: boolean;
	/** The title to display when no items match the search. */
	emptyStateTitle?: string;
	/** The description to display when no items match the search. */
	emptyStateDescription?: string;
	/** Custom formatter for the selected count text in the trigger. */
	selectedCountFormatter?: (count: number) => ReactNode;
	/** Supporting text displayed next to the selected count in the trigger. */
	supportingText?: ReactNode;
	/** Leading icon component displayed in the trigger. */
	icon?: FC | ReactNode;
}

const MultiSelectRoot = ({
	items,
	children,
	size = "md",
	selectedKeys,
	defaultSelectedKeys,
	onSelectionChange,
	isDisabled,
	isRequired,
	isInvalid,
	placeholder = "Select",
	label,
	hint,
	tooltip,
	hideRequiredIndicator,
	popoverClassName,
	className,
	onReset,
	onSelectAll,
	showFooter = true,
	showSearch = true,
	emptyStateTitle,
	emptyStateDescription,
	selectedCountFormatter,
	supportingText,
	icon,
}: MultiSelectProps) => {
	const { contains } = useFilter({ sensitivity: "base" });
	const [searchValue, setSearchValue] = useState("");

	const triggerRef = useRef<HTMLButtonElement>(null);
	const [popoverWidth, setPopoverWidth] = useState("");

	const onResize = useCallback(() => {
		if (!triggerRef.current) {
			return;
		}
		const rect = triggerRef.current.getBoundingClientRect();
		setPopoverWidth(`${rect.width}px`);
	}, []);

	const selectedCount = getSelectedCount(selectedKeys, items?.length ?? 0);
	const hasSelection = selectedCount > 0;
	const triggerIcon = resolveTriggerIcon(icon);

	const handleClearSearch = useCallback(() => {
		setSearchValue("");
	}, []);

	const clearSearchAction =
		searchValue.length > 0 ? handleClearSearch : undefined;
	return (
		<SelectContext.Provider value={{ size }}>
			<div
				className={cx(
					"flex w-full min-w-0 max-w-full flex-col gap-1.5",
					className
				)}
			>
				{!!label && (
					<Label
						isRequired={hideRequiredIndicator ? false : !!isRequired}
						tooltip={tooltip}
					>
						{label}
					</Label>
				)}

				<AriaDialogTrigger>
					<AriaButton
						className={(state) =>
							cx(
								"relative flex w-full min-w-0 max-w-full cursor-pointer items-center overflow-hidden rounded-lg bg-primary shadow-xs outline-hidden ring-1 ring-primary ring-inset transition duration-100 ease-linear",
								(!!state.isFocusVisible || !!state.isPressed) &&
									"ring-2 ring-brand",
								!!state.isDisabled && "cursor-not-allowed! opacity-50"
							)
						}
						isDisabled={isDisabled}
						onClick={onResize}
						ref={triggerRef}
					>
						<span
							className={cx(
								"flex w-full min-w-0 max-w-full items-center overflow-hidden text-left",
								sizes[size].root,
								"*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary"
							)}
						>
							{triggerIcon}

							{hasSelection ? (
								<span
									className={cx(
										"flex min-w-0 flex-1 items-center truncate",
										sizes[size].textContainer
									)}
								>
									<span
										className={cx(
											"truncate font-medium text-primary",
											sizes[size].text
										)}
									>
										{selectedCountFormatter
											? selectedCountFormatter(selectedCount)
											: `${selectedCount} selected`}
									</span>
									{!!supportingText && (
										<span
											className={cx("truncate text-tertiary", sizes[size].text)}
										>
											{supportingText}
										</span>
									)}
								</span>
							) : (
								<span
									className={cx(
										"min-w-0 flex-1 truncate text-placeholder",
										sizes[size].text
									)}
								>
									{placeholder}
								</span>
							)}

							<ChevronDown
								aria-hidden="true"
								className={cx(
									"ml-auto shrink-0 text-fg-quaternary",
									size === "lg" ? "size-5" : "size-4 stroke-[2.25px]"
								)}
							/>
						</span>
					</AriaButton>

					<AriaPopover
						className={(state) =>
							cx(
								"w-(--trigger-width) origin-(--trigger-anchor-point) overflow-hidden rounded-lg bg-primary shadow-lg outline-hidden ring-1 ring-secondary_alt will-change-transform",
								!!state.isEntering &&
									"fade-in placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5 animate-in duration-150 ease-out",
								!!state.isExiting &&
									"fade-out placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5 animate-out duration-100 ease-in",
								popoverClassName
							)
						}
						containerPadding={0}
						offset={4}
						placement="bottom"
						style={{ width: popoverWidth || undefined }}
					>
						<AriaDialog className="outline-hidden">
							<AriaAutocomplete
								filter={contains}
								inputValue={searchValue}
								onInputChange={setSearchValue}
							>
								{!!showSearch && (
									<div
										className={cx(
											"border-secondary border-b",
											searchSizes[size].wrapper
										)}
									>
										<AriaSearchField
											aria-label="Buscar"
											autoFocus
											onChange={setSearchValue}
											value={searchValue}
										>
											<div
												className={cx(
													"flex items-center",
													searchSizes[size].root
												)}
											>
												<SearchLg
													aria-hidden="true"
													className="shrink-0 text-fg-quaternary"
													data-icon
												/>
												<AriaInput
													className={cx(
														"w-full appearance-none bg-transparent text-primary caret-alpha-black/90 outline-hidden placeholder:text-placeholder",
														searchSizes[size].text
													)}
													placeholder="Buscar"
												/>
											</div>
										</AriaSearchField>
									</div>
								)}

								<AriaListBox
									aria-label={label || "Options"}
									className={cx(
										"overflow-y-auto py-1 outline-hidden",
										popoverMaxHeights[size]
									)}
									defaultSelectedKeys={defaultSelectedKeys}
									items={items}
									onSelectionChange={onSelectionChange}
									renderEmptyState={() => (
										<MultiSelectEmptyState
											description={emptyStateDescription}
											onClearSearch={clearSearchAction}
											title={emptyStateTitle}
										/>
									)}
									selectedKeys={selectedKeys}
									selectionBehavior="toggle"
									selectionMode="multiple"
								>
									{children}
								</AriaListBox>
							</AriaAutocomplete>

							{!!showFooter && (
								<MultiSelectFooter
									onReset={onReset}
									onSelectAll={onSelectAll}
									size={size}
								/>
							)}
						</AriaDialog>
					</AriaPopover>
				</AriaDialogTrigger>

				{!!hint && (
					<HintText
						className={cx(size === "sm" && "text-xs")}
						isInvalid={isInvalid}
					>
						{hint}
					</HintText>
				)}
			</div>
		</SelectContext.Provider>
	);
};

const MultiSelect = MultiSelectRoot as typeof MultiSelectRoot & {
	Item: typeof SelectItem;
	Footer: typeof MultiSelectFooter;
	EmptyState: typeof MultiSelectEmptyState;
};

MultiSelect.Item = SelectItem;
MultiSelect.Footer = MultiSelectFooter;
MultiSelect.EmptyState = MultiSelectEmptyState;

export { MultiSelect };
